'use client'

import React, { useState } from 'react'
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab
} from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { alpha } from '@mui/material/styles'
import { ColorBox } from 'src/Components/Ui/ColorBox'
import { muiGrey } from 'src/style/muiTheme'
import { attentionBorderPulseSx } from 'src/style/attention'
import { useLocale } from 'src/locales/useLocale'
import { getPriceUnit } from 'src/util'

const interpolate = (template: string, vars: Record<string, string>) =>
  Object.entries(vars).reduce((str, [key, value]) => str.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value), template)

const toTaxIncludedPrice = (price: number) => Math.round(price * 1.1)

type SelectCardWithConfirmLabels = {
  openLabel: (summary: string) => string
  dialogTitle: (summary: string) => string
  selectedBadge: string
  taxIncludedPrice: (price: number) => string
  availableColorsCatchFace: string
  cancel: string
  select: string
  selectOption: (label: string) => string
}

const useSelectCardWithConfirmLabels = (): SelectCardWithConfirmLabels => {
  const { t, locale } = useLocale()

  return {
    openLabel: (summary) => interpolate(t.SELECT_CARD_WITH_CONFIRM_OPEN_LABEL, { summary }),
    dialogTitle: (summary) => interpolate(t.SELECT_CARD_WITH_CONFIRM_DIALOG_TITLE, { summary }),
    selectedBadge: t.SELECT_CARD_WITH_CONFIRM_SELECTED_BADGE,
    taxIncludedPrice: (price) =>
      interpolate(t.SELECT_CARD_WITH_CONFIRM_TAX_INCLUDED, {
        price: toTaxIncludedPrice(price).toLocaleString(),
        unit: getPriceUnit(locale)
      }),
    availableColorsCatchFace: t.SELECT_CARD_WITH_CONFIRM_AVAILABLE_COLORS_CATCH_FACE,
    cancel: t.SELECT_CARD_WITH_CONFIRM_CANCEL,
    select: t.SELECT_CARD_WITH_CONFIRM_SELECT,
    selectOption: (label) => interpolate(t.SELECT_CARD_WITH_CONFIRM_SELECT_OPTION, { label })
  }
}

export type SelectCardWithConfirmColor = {
  label: string
  value: string
  color: string
}

export type SelectCardWithConfirmObject = {
  label: string
  value: string
  color?: string
  confirmPrice?: number
  confirmDescription?: React.ReactNode
  confirmColors?: SelectCardWithConfirmColor[]
}

const selectedBgSx = (isDark?: boolean) =>
  isDark ? { bgcolor: '#1a1a1a' } : { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) }

const ConfirmPrice: React.FC<{
  price: number
  isDark?: boolean
  size?: 'normal' | 'small'
  taxIncludedPrice: (price: number) => string
}> = ({ price, isDark, size = 'normal', taxIncludedPrice }) => (
  <Box
    fontSize={size === 'small' ? { xs: 11, sm: 12 } : { xs: 13, sm: 14 }}
    fontWeight="bold"
    color={isDark ? '#ffb74d' : 'error.main'}
  >
    {taxIncludedPrice(price)}
  </Box>
)

const ConfirmColorList: React.FC<{
  colors: SelectCardWithConfirmColor[]
  isDark?: boolean
  size?: 'normal' | 'small'
}> = ({ colors, isDark, size = 'normal' }) => (
  <Box display="flex" flexWrap="wrap" gap={size === 'small' ? 0.75 : 1} mt={size === 'small' ? 0.5 : 1}>
    {colors.map((colorOption) => (
      <Box
        key={colorOption.value}
        display="flex"
        alignItems="center"
        fontSize={size === 'small' ? { xs: 10, sm: 11 } : { xs: 12, sm: 13 }}
        color={isDark ? '#e8e8e8' : 'text.primary'}
        sx={{ lineHeight: 1.2 }}
      >
        <ColorBox bgcolor={colorOption.color} />
        {colorOption.label}
      </Box>
    ))}
  </Box>
)

const OptionDetailContent: React.FC<{
  obj: SelectCardWithConfirmObject
  labels: SelectCardWithConfirmLabels
  isDark?: boolean
  isExpanded: boolean
  showSelectedBadge?: boolean
  showLabel?: boolean
  showDescription?: boolean
}> = ({
  obj,
  labels,
  isDark,
  isExpanded,
  showSelectedBadge = false,
  showLabel = true,
  showDescription = true
}) => (
  <>
    {showLabel && (
      <Box
        display="flex"
        alignItems="center"
        fontWeight="bold"
        fontSize={isExpanded ? { xs: 15, sm: 17 } : { xs: 12, sm: 13 }}
        color={isDark ? '#fff' : isExpanded ? 'text.primary' : '#333'}
        mb={
          obj.confirmPrice !== undefined || (showDescription && obj.confirmDescription) || isExpanded
            ? isExpanded
              ? 1
              : 0.25
            : 0
        }
      >
        {obj.color && <ColorBox bgcolor={obj.color} />}
        {obj.label}
        {showSelectedBadge && (
          <Box component="span" ml={1} fontSize={{ xs: 12, sm: 13 }} color={isDark ? '#2ebdff' : 'primary.main'}>
            {labels.selectedBadge}
          </Box>
        )}
      </Box>
    )}
    {obj.confirmPrice !== undefined && (
      <Box mb={isExpanded && showDescription && obj.confirmDescription ? 1 : 0}>
        <ConfirmPrice
          price={obj.confirmPrice}
          isDark={isDark}
          size={isExpanded ? 'normal' : 'small'}
          taxIncludedPrice={labels.taxIncludedPrice}
        />
      </Box>
    )}
    {showDescription && obj.confirmDescription && (
      <Box
        fontSize={isExpanded ? { xs: 13, sm: 14 } : { xs: 11, sm: 12 }}
        lineHeight={isExpanded ? 1.7 : 1.4}
        color={isExpanded ? 'inherit' : isDark ? '#e0e0e0' : 'text.secondary'}
        sx={
          isExpanded
            ? { whiteSpace: 'pre-line' }
            : {
                whiteSpace: 'pre-line',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }
        }
      >
        {obj.confirmDescription}
      </Box>
    )}
    {isExpanded && obj.confirmColors && obj.confirmColors.length > 0 && (
      <Box mt={1.5}>
        <Box fontSize={{ xs: 12, sm: 13 }} fontWeight="bold" mb={0.5} color={isDark ? '#e0e0e0' : 'text.primary'}>
          {labels.availableColorsCatchFace}
        </Box>
        <ConfirmColorList colors={obj.confirmColors} isDark={isDark} />
      </Box>
    )}
  </>
)

const ConfirmOptionCard: React.FC<{
  obj: SelectCardWithConfirmObject
  labels: SelectCardWithConfirmLabels
  isSelected: boolean
  isDark?: boolean
  onSelect: (value: string) => void
}> = ({ obj, labels, isSelected, isDark, onSelect }) => (
  <Box
    onClick={() => onSelect(obj.value)}
    px={{ xs: isSelected ? 1.5 : 1, sm: isSelected ? 2 : 1.25 }}
    py={{ xs: isSelected ? 1.5 : 0.75, sm: isSelected ? 2 : 1 }}
    borderRadius={1}
    border={isSelected ? 2 : 1}
    borderColor={isSelected ? 'primary.main' : isDark ? '#888' : '#bdbdbd'}
    bgcolor={isSelected ? undefined : isDark ? '#505050' : '#fff'}
    sx={(theme) => ({
      cursor: 'pointer',
      ...(isSelected ? selectedBgSx(isDark) : {}),
      ...(isSelected ? attentionBorderPulseSx(theme, 'primary', { iterations: 'infinite' }) : {})
    })}
  >
    <OptionDetailContent
      obj={obj}
      labels={labels}
      isDark={isDark}
      isExpanded={isSelected}
      showSelectedBadge={isSelected}
    />
  </Box>
)

const SelectedOptionSummary: React.FC<{
  obj: SelectCardWithConfirmObject
  labels: SelectCardWithConfirmLabels
  isDark?: boolean
}> = ({ obj, labels, isDark }) => (
  <Box
    px={{ xs: 1.5, sm: 2 }}
    py={{ xs: 1.5, sm: 2 }}
    borderRadius={1}
    border={2}
    borderColor="primary.main"
    textAlign="left"
    sx={selectedBgSx(isDark)}
  >
    <OptionDetailContent obj={obj} labels={labels} isDark={isDark} isExpanded showLabel={false} showDescription={false} />
  </Box>
)

type Props = {
  summary: string
  objects: SelectCardWithConfirmObject[]
  selectedLabel?: string
  selectedValue?: string
  selectedColor?: string
  disabled?: boolean
  setTarget?: boolean
  description?: string
  caution?: string
  defaultExpanded?: boolean
  isDisplay?: boolean
  isError?: boolean
  index?: number
  className?: string
  isDark?: boolean
  openButtonLabel?: string
  confirmDialogTitle?: string
  confirmDialogCaution?: string
  handleChange: (selected: string, i?: number) => void
}

export const SelectCardWithConfirm: React.FC<Props> = ({
  summary,
  objects,
  selectedLabel,
  selectedValue,
  selectedColor,
  disabled,
  description,
  caution,
  defaultExpanded = true,
  isDisplay = true,
  isError,
  index,
  className,
  isDark,
  openButtonLabel,
  confirmDialogTitle,
  confirmDialogCaution,
  handleChange
}) => {
  const labels = useSelectCardWithConfirmLabels()
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded)
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [pendingValue, setPendingValue] = useState<string | null>(null)
  const pendingObject = objects.find((obj) => obj.value === pendingValue)
  const selectedObject = objects.find((obj) => obj.value === selectedValue)
  const hasSelection =
    selectedValue !== undefined && selectedValue !== 'unselected' && selectedObject !== undefined

  const handleOpenDialog = () => {
    setPendingValue(objects[0]?.value ?? null)
    setIsDialogOpen(true)
  }

  const handleDialogClose = () => {
    setIsDialogOpen(false)
    setPendingValue(null)
  }

  const handleConfirm = () => {
    if (pendingValue === null) return
    handleChange(pendingValue, index)
    handleDialogClose()
  }

  if (!isDisplay) return <></>

  return (
    <>
      <Accordion
        style={isDark ? { marginBottom: '16px', background: '#282828' } : { marginBottom: '16px' }}
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
      >
        <AccordionSummary
          expandIcon={
            <Fab size="small" tabIndex={-1} style={{ boxShadow: 'unset' }}>
              <ExpandLessIcon style={isDark ? { color: 'white' } : {}} />
            </Fab>
          }
        >
          <Box display="flex">
            <Box fontSize={14} fontWeight="bold" color={isDark ? '#fff' : muiGrey}>
              {summary}
            </Box>
            <Box
              component="span"
              display="inherit"
              color={isError ? (isDark ? 'orange' : 'red') : isDark ? 'white' : muiGrey}
              fontSize={15}
              alignItems="center"
              ml={2}
              fontWeight={isDark ? 'bold' : 'normal'}
            >
              {selectedColor && <ColorBox bgcolor={selectedColor} />}
              {selectedLabel}
            </Box>
          </Box>
        </AccordionSummary>
        {caution && (
          <Box
            pl={3}
            textAlign="left"
            color={isDark ? 'orange' : 'red'}
            fontSize={'12px'}
            fontWeight={isDark ? 'bold' : 'unset'}
            mb={1}
          >
            {caution}
          </Box>
        )}
        {description && (
          <Box
            pl={3}
            textAlign="left"
            color={isDark ? '#2ebdff' : 'blue'}
            fontSize={'12px'}
            fontWeight={isDark ? 'bold' : 'normal'}
          >
            {description}
          </Box>
        )}
        <AccordionDetails>
          <Box px={1} mb={1} textAlign="left">
            {hasSelection ? (
              <SelectedOptionSummary obj={selectedObject} labels={labels} isDark={isDark} />
            ) : (
              <Button
                onClick={handleOpenDialog}
                variant="outlined"
                disabled={disabled}
                style={
                  isDark
                    ? {
                        textTransform: 'none',
                        padding: '15px',
                        color: '#fff',
                        borderColor: 'black',
                        backgroundColor: disabled ? '#737373' : '#505050',
                        fontWeight: 'bold'
                      }
                    : {
                        textTransform: 'none',
                        padding: '15px'
                      }
                }
                className={className}
              >
                {openButtonLabel ?? labels.openLabel(summary)}
              </Button>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="select-card-confirm-dialog-title"
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: '12px', sm: '24px' },
            width: {
              xs: 'calc(100% - 24px)',
              sm: 'calc(100% - 48px)',
              md: 900
            },
            maxWidth: {
              xs: 'calc(100% - 24px)',
              sm: 'calc(100% - 48px)',
              md: 900
            },
            minWidth: {
              sm: 'calc(100% - 48px)',
              md: 900
            },
            maxHeight: { xs: '92vh', sm: '85vh' }
          }
        }}
      >
        <DialogTitle
          id="select-card-confirm-dialog-title"
          sx={{
            fontWeight: 'bold',
            fontSize: { xs: 16, sm: 18 },
            backgroundColor: isDark ? '#282828' : 'unset',
            color: isDark ? '#fff' : 'inherit'
          }}
        >
          {confirmDialogTitle ?? labels.dialogTitle(summary)}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            backgroundColor: isDark ? '#282828' : 'unset',
            color: isDark ? '#fff' : 'inherit',
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 3 }
          }}
        >
          <Box display="flex" flexDirection="column" gap={{ xs: 1, sm: 1.5 }}>
            {objects.map((obj) => (
              <ConfirmOptionCard
                key={obj.value}
                obj={obj}
                labels={labels}
                isSelected={obj.value === pendingValue}
                isDark={isDark}
                onSelect={setPendingValue}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            backgroundColor: isDark ? '#282828' : 'unset',
            justifyContent: 'space-between',
            alignItems: 'flex-end'
          }}
        >
          <Button
            variant="outlined"
            onClick={handleDialogClose}
            sx={isDark ? { color: '#fff', borderColor: '#737373', fontWeight: 'bold' } : {}}
          >
            {labels.cancel}
          </Button>
          <Box textAlign="right">
            {confirmDialogCaution && (
              <Box fontSize={{ xs: 11, sm: 12 }} fontWeight="bold" color={isDark ? 'orange' : 'error.main'} mb={0.75}>
                {confirmDialogCaution}
              </Box>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={pendingValue === null}
              autoFocus
              sx={isDark ? { color: '#fff', backgroundColor: 'black', fontWeight: 'bold' } : {}}
            >
              {pendingObject ? labels.selectOption(pendingObject.label) : labels.select}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}
